export function normalizeManualClassification(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

export function toUnixSeconds(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
}

function qtyKey(value) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n.toFixed(12) : "0.000000000000";
}

function pickHash(tx = {}) {
  return String(
    tx.hash ||
      tx.txid ||
      tx.transactionHash ||
      tx.event_id ||
      tx.eventId ||
      tx.raw_json?.id ||
      ""
  ).toLowerCase();
}

function pickToken(tx = {}) {
  return String(
    tx.token ||
      tx.token_symbol ||
      tx.tokenSymbol ||
      tx.symbol ||
      tx.asset ||
      ""
  )
    .trim()
    .toUpperCase();
}

function pickTokenContract(tx = {}) {
  return String(
    tx.token_contract ||
      tx.tokenContract ||
      tx.contract ||
      tx.contractAddress ||
      tx.emitter ||
      ""
  ).toLowerCase();
}

function pickFrom(tx = {}) {
  return String(tx.from || tx.sender || "").toLowerCase();
}

function pickTo(tx = {}) {
  return String(tx.to || tx.recipient || tx.receiver || "").toLowerCase();
}

function pickClause(tx = {}) {
  return String(
    tx.clause_index ??
      tx.clauseIndex ??
      tx.raw_json?.clause_index ??
      tx.raw_json?.clauseIndex ??
      tx.raw_json?.clause ??
      ""
  ).toLowerCase();
}

function pickEvent(tx = {}) {
  return String(
    tx.event_index ??
      tx.eventIndex ??
      tx.log_index ??
      tx.logIndex ??
      tx.raw_json?.event_index ??
      tx.raw_json?.eventIndex ??
      tx.raw_json?.log_index ??
      tx.raw_json?.logIndex ??
      ""
  ).toLowerCase();
}

function pickSubEventId(tx = {}) {
  return String(
    tx.subEventId ??
      tx.sub_event_id ??
      tx.event_id ??
      tx.eventId ??
      tx.log_index ??
      tx.logIndex ??
      tx.event_index ??
      tx.eventIndex ??
      tx.idx ??
      tx.raw_json?.event_id ??
      tx.raw_json?.eventId ??
      ""
  ).toLowerCase();
}

function pickAmountForKey(tx = {}) {
  return Number(
    tx.amount_raw ??
      tx.raw_amount ??
      tx.amount ??
      tx.value ??
      tx.quantity ??
      tx.qty ??
      0
  ) || 0;
}

export function buildTxOverrideKey(tx = {}) {
  const ts = toUnixSeconds(
    tx.timestamp ?? tx.ts ?? tx.block_timestamp ?? tx.raw_json?.block_timestamp
  );

  return [
    pickHash(tx),
    String(ts),
    pickToken(tx),
    pickTokenContract(tx),
    pickFrom(tx),
    pickTo(tx),
    qtyKey(pickAmountForKey(tx)),
    pickClause(tx),
    pickEvent(tx),
    pickSubEventId(tx),
    String(tx.__source || tx.source || "").toUpperCase(),
  ].join("|");
}

export function buildManualOverridePayload(tx = {}, classification) {
  const cls = normalizeManualClassification(classification);
  const key = tx.manualOverrideKey || buildTxOverrideKey(tx);

  return {
    key,
    classification: cls,
    originalClassification: normalizeManualClassification(
      tx.originalClassification || tx.classification || tx.type || tx.event || ""
    ),
    txHash: pickHash(tx),
    token: pickToken(tx),
    tokenContract: pickTokenContract(tx),
    from: pickFrom(tx),
    to: pickTo(tx),
    amount: pickAmountForKey(tx),
    timestamp: toUnixSeconds(
      tx.timestamp ?? tx.ts ?? tx.block_timestamp ?? tx.raw_json?.block_timestamp
    ),
    source: String(tx.__source || tx.source || "").toUpperCase(),
    clauseIndex: pickClause(tx),
    eventIndex: pickEvent(tx),
    subEventId: pickSubEventId(tx),
    updatedAt: new Date().toISOString(),
  };
}

export function pickManualClassification(manualClassifications = {}, tx = {}) {
  const key = tx.manualOverrideKey || buildTxOverrideKey(tx);

  return (
    manualClassifications[key] ||
    manualClassifications[tx.manualOverrideKey] ||
    manualClassifications[tx.overrideKey] ||
    manualClassifications[tx.hash] ||
    null
  );
}

export function buildManualClassificationMapFromResult(resultLike = {}) {
  const ds =
    resultLike?.dataset ||
    resultLike?.analysisResult?.dataset ||
    resultLike?.processedData ||
    resultLike ||
    {};

  const fromMeta = ds.manualClassificationOverrides || {};
  const fromRows = {};

  const allRows = [
    ...(Array.isArray(ds.vip180Data) ? ds.vip180Data : []),
    ...(Array.isArray(ds.vetVthoData) ? ds.vetVthoData : []),
  ];

  for (const row of allRows) {
    if (!row?.isManualOverride && !row?.manualOverride) continue;

    const key = row.manualOverrideKey || buildTxOverrideKey(row);
    const cls = row.manualClassification || row.classification || row.type;

    if (key && cls) {
      fromRows[key] = cls;
    }
  }

  return {
    ...fromMeta,
    ...fromRows,
  };
}
